import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import MonacoEditor from '@monaco-editor/react';
import { 
  Maximize2, 
  Minimize2, 
  Type,
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered
} from 'lucide-react';

const EditorContainer = styled(motion.div)`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: ${props => props.theme.colors.background};
  position: relative;
`;

const EditorHeader = styled.div`
  height: 50px;
  background: ${props => props.theme.colors.surface};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
`;

const EditorTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${props => props.theme.colors.text};
  font-size: 14px;
  font-weight: 500;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ToolbarButton = styled(motion.button)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: ${props => props.theme.colors.text};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.colors.hover};
    color: ${props => props.theme.colors.primary};
  }

  &.active {
    background: ${props => props.theme.colors.primary};
    color: #ffffff;
  }
`;

const EditorWrapper = styled.div`
  flex: 1;
  position: relative;
`;

const WordCount = styled.div`
  position: absolute;
  bottom: 20px;
  right: 20px;
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 20px;
  padding: 8px 16px;
  font-size: 12px;
  color: ${props => props.theme.colors.textSecondary};
  backdrop-filter: blur(10px);
  z-index: 10;
`;

function Editor({ content, onChange }) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fontSize, setFontSize] = useState(14);
  const [wordCount, setWordCount] = useState(0);
  const [charCount, setCharCount] = useState(0);
  const editorRef = useRef(null);

  useEffect(() => {
    // 단어 수와 글자 수 계산
    const words = content.trim() ? content.trim().split(/\s+/).length : 0;
    const chars = content.length;
    setWordCount(words);
    setCharCount(chars);
  }, [content]);

  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor;
    
    // 다크 테마 설정
    monaco.editor.defineTheme('custom-dark', {
      base: 'vs-dark',
      inherit: true,
      rules: [],
      colors: {
        'editor.background': '#1e1e1e',
        'editor.foreground': '#d4d4d4',
        'editorLineNumber.foreground': '#858585',
        'editorLineNumber.activeForeground': '#c6c6c6',
        'editor.selectionBackground': '#264f78',
        'editor.selectionHighlightBackground': '#add6ff26',
        'editorCursor.foreground': '#aeafad',
        'editor.findMatchBackground': '#515c6a',
        'editor.findMatchHighlightBackground': '#ea5c0055',
        'editor.hoverHighlightBackground': '#264f7840',
        'editor.lineHighlightBackground': '#2a2d2e',
        'editorIndentGuide.background': '#404040',
        'editorIndentGuide.activeBackground': '#707070',
        'editorBracketMatch.background': '#0e639c50',
        'editorBracketMatch.border': '#888888'
      }
    });

    monaco.editor.setTheme('custom-dark');

    // 단축키 설정
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
      // 저장 기능은 상위 컴포넌트에서 처리
      console.log('저장 단축키');
    });

    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyN, () => {
      onChange('');
    });
  };

  const handleEditorChange = (value) => {
    onChange(value || '');
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };


  const formatText = (format) => {
    if (editorRef.current) {
      const selection = editorRef.current.getSelection();
      const selectedText = editorRef.current.getModel().getValueInRange(selection);
      
      let formattedText = selectedText;
      switch (format) {
        case 'bold':
          formattedText = `**${selectedText}**`;
          break;
        case 'italic':
          formattedText = `*${selectedText}*`;
          break;
        case 'underline':
          formattedText = `<u>${selectedText}</u>`;
          break;
        default:
          return;
      }
      
      editorRef.current.executeEdits('format-text', [{
        range: selection,
        text: formattedText
      }]);
    }
  };

  return (
    <EditorContainer
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <EditorHeader>
        <EditorTitle>
          <Type size={16} />
          문서 편집기
        </EditorTitle>

        <Toolbar>
          <ToolbarButton
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => setFontSize(prev => Math.max(prev - 1, 10))}
          >
            A-
          </ToolbarButton>

          <ToolbarButton
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => setFontSize(prev => Math.min(prev + 1, 24))}
          >
            A+
          </ToolbarButton>

          <div style={{ width: '1px', height: '20px', background: '#404040', margin: '0 8px' }} />

          <ToolbarButton
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => formatText('bold')}
          >
            <Bold size={14} />
          </ToolbarButton>

          <ToolbarButton
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => formatText('italic')}
          >
            <Italic size={14} />
          </ToolbarButton>

          <ToolbarButton
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => formatText('underline')}
          >
            <Underline size={14} />
          </ToolbarButton>

          <div style={{ width: '1px', height: '20px', background: '#404040', margin: '0 8px' }} />

          <ToolbarButton
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <List size={14} />
          </ToolbarButton>

          <ToolbarButton
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <ListOrdered size={14} />
          </ToolbarButton>

          <div style={{ width: '1px', height: '20px', background: '#404040', margin: '0 8px' }} />

          <ToolbarButton
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={toggleFullscreen}
          >
            {isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          </ToolbarButton>
        </Toolbar>
      </EditorHeader>

      <EditorWrapper>
        <MonacoEditor
          height="100%"
          defaultLanguage="markdown"
          value={content}
          onChange={handleEditorChange}
          onMount={handleEditorDidMount}
          options={{
            fontSize: fontSize,
            fontFamily: 'JetBrains Mono, Consolas, monospace',
            lineNumbers: 'on',
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            automaticLayout: true,
            wordWrap: 'on',
            wrappingIndent: 'indent',
            suggestOnTriggerCharacters: true,
            acceptSuggestionOnEnter: 'on',
            tabCompletion: 'on',
            wordBasedSuggestions: true,
            renderLineHighlight: 'all',
            renderWhitespace: 'selection',
            cursorBlinking: 'smooth',
            cursorSmoothCaretAnimation: 'on',
            smoothScrolling: true,
            contextmenu: true,
            mouseWheelZoom: true,
            padding: { top: 20, bottom: 20 }
          }}
          theme="custom-dark"
        />
      </EditorWrapper>

      <WordCount>
        {wordCount} 단어 • {charCount} 글자
      </WordCount>
    </EditorContainer>
  );
}

export default Editor;
